import {
  COMPANION_FIELD_ROWS,
  DEFAULT_PRIMARY_GUEST_FIELDS,
  normalizeGuestAddressFieldModes,
  type CompanionFieldKey,
  type CompanionFieldsConfig,
} from "@/lib/companionFields";
import { addDaysLocal, formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import type { Guest, PitchCategory } from "@/types/camping";
import type { DepositSettings } from "@/pages/stammdaten/settings/tenantSettingsTypes";

export interface MailReplySeed {
  email: string;
  firstName: string;
  lastName: string;
  arrivalDate: string;
  departureDate: string;
  categoryId: string;
  arrivalType: string;
  adults: number | null;
  children: number | null;
  dogs: number | null;
  wantsElectricity: boolean | null;
  guestId: string;
  notes: string;
  placeBanned: boolean;
  missingLabels: string[];
  presentLabels: string[];
  depositHint: string;
}

export interface MailInquiryAnalysis {
  erasureRequest: boolean;
  arrivalDate: string;
  departureDate: string;
  adults: number | null;
  children: number | null;
  dogs: number | null;
  arrivalType: string;
  categoryId: string;
  categoryName: string;
  wantsElectricity: boolean | null;
  summary: string;
}

const MONTHS: Record<string, number> = {
  januar: 1,
  jan: 1,
  februar: 2,
  feb: 2,
  märz: 3,
  maerz: 3,
  mär: 3,
  mar: 3,
  april: 4,
  apr: 4,
  mai: 5,
  juni: 6,
  jun: 6,
  juli: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sept: 9,
  sep: 9,
  oktober: 10,
  okt: 10,
  november: 11,
  nov: 11,
  dezember: 12,
  dez: 12,
};

const MONTH_PATTERN =
  "januar|jan|februar|feb|märz|maerz|mär|mar|april|apr|mai|juni|jun|juli|jul|august|aug|september|sept|sep|oktober|okt|november|nov|dezember|dez";

const RANGE_JOIN =
  String.raw`\s*(?:bis(?:\s+(?:zum|einschließlich|einschliesslich))?|–|—|-|to|until)\s*`;

const WORD_NUMBERS: Record<string, number> = {
  ein: 1,
  eine: 1,
  einen: 1,
  einem: 1,
  einer: 1,
  eins: 1,
  zwei: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
  fuenf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  elf: 11,
  zwölf: 12,
  zwoelf: 12,
};

const COUNT_WORD =
  "\\d{1,2}|ein|eine|einen|einem|einer|eins|zwei|drei|vier|fünf|fuenf|sechs|sieben|acht|neun|zehn|elf|zwölf|zwoelf";

const ARRIVAL_TYPE_LABELS: Record<string, string> = {
  motorhome: "Wohnmobil",
  caravan: "Wohnwagen",
  tent: "Zelt",
  tent_car: "Zelt mit Auto",
  tent_bike: "Zelt mit Fahrrad",
  tow_vehicle: "Zugfahrzeug",
};

interface RawDate {
  day: number;
  month: number;
  year: number | null;
}

interface StayRange {
  index: number;
  arrival: string;
  departure: string;
  kind: "range" | "nights" | "week";
}

function toIsoDate(day: string, month: string, year: string): string | null {
  const d = Number(day);
  const m = Number(month);
  let y = Number(year);
  if (year.length === 2) y += y >= 70 ? 1900 : 2000;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null;
  const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const check = new Date(`${iso}T12:00:00`);
  if (check.getFullYear() !== y || check.getMonth() + 1 !== m || check.getDate() !== d) return null;
  return iso;
}

function expandYear(year: number): number {
  if (year >= 100) return year;
  return year + (year >= 70 ? 1900 : 2000);
}

function isoFromParts(day: number, month: number, year: number): string | null {
  return toIsoDate(String(day), String(month), String(year));
}

function finalizePair(
  start: RawDate,
  end: RawDate,
  today: string,
  checkoutExtraDay = false,
): { arrival: string; departure: string } | null {
  let startYear = start.year == null ? null : expandYear(start.year);
  let endYear = end.year == null ? null : expandYear(end.year);

  if (startYear != null && endYear == null) {
    endYear = startYear;
    const arrival = isoFromParts(start.day, start.month, startYear);
    const departure = isoFromParts(end.day, end.month, endYear);
    if (arrival && departure && departure <= arrival) endYear = startYear + 1;
  } else if (startYear == null && endYear != null) {
    startYear = endYear;
    const arrival = isoFromParts(start.day, start.month, startYear);
    const departure = isoFromParts(end.day, end.month, endYear);
    if (arrival && departure && arrival >= departure) startYear = endYear - 1;
  } else if (startYear == null && endYear == null) {
    startYear = Number(today.slice(0, 4));
    endYear = startYear;
    const arrival = isoFromParts(start.day, start.month, startYear);
    const departure = isoFromParts(end.day, end.month, endYear);
    if (arrival && departure && departure <= arrival) endYear = startYear + 1;
    const rolled = isoFromParts(start.day, start.month, startYear);
    if (rolled && rolled < today) {
      startYear += 1;
      endYear += 1;
    }
  }

  if (startYear == null || endYear == null) return null;
  const arrival = isoFromParts(start.day, start.month, startYear);
  let departure = isoFromParts(end.day, end.month, endYear);
  if (!arrival || !departure) return null;
  if (checkoutExtraDay) departure = addDaysLocal(departure, 1);
  if (departure <= arrival || arrival < today) return null;
  return { arrival, departure };
}

function monthNumber(name: string): number | null {
  return MONTHS[name.toLocaleLowerCase("de")] ?? null;
}

function pushRange(
  ranges: StayRange[],
  index: number,
  start: RawDate,
  end: RawDate,
  today: string,
  checkoutExtraDay: boolean,
) {
  const pair = finalizePair(start, end, today, checkoutExtraDay);
  if (!pair) return;
  ranges.push({ index, arrival: pair.arrival, departure: pair.departure, kind: "range" });
}

function resolveSingleDate(raw: RawDate, today: string): string | null {
  let year = raw.year == null ? null : expandYear(raw.year);
  if (year == null) {
    year = Number(today.slice(0, 4));
    const thisYear = isoFromParts(raw.day, raw.month, year);
    if (thisYear && thisYear < today) year += 1;
  }
  const iso = isoFromParts(raw.day, raw.month, year);
  if (!iso || iso < today) return null;
  return iso;
}

function collectStayRanges(flat: string, today: string): StayRange[] {
  const ranges: StayRange[] = [];
  const inclusive = (between: string) => /einschließlich|einschliesslich/i.test(between);

  const labeledStart = flat.match(
    /(?:anreise|ankunft|check-?in|arrival)\s*[:\-]?\s*(.{0,48})/i,
  );
  const labeledEnd = flat.match(
    /(?:abreise|abfahrt|check-?out|departure)\s*[:\-]?\s*(.{0,48})/i,
  );
  if (labeledStart?.[1] && labeledEnd?.[1] && labeledStart.index != null && labeledEnd.index != null) {
    const startHit = firstRawDate(labeledStart[1]);
    const endHit = firstRawDate(labeledEnd[1]);
    if (startHit && endHit && labeledEnd.index > labeledStart.index) {
      pushRange(ranges, labeledStart.index, startHit, endHit, today, false);
    }
  }

  const numeric = new RegExp(
    String.raw`\b(\d{1,2})\.(\d{1,2})\.?(?:(\d{2,4})\b)?${RANGE_JOIN}(\d{1,2})\.(\d{1,2})\.?(?:(\d{2,4})\b)?`,
    "giu",
  );
  for (const match of flat.matchAll(numeric)) {
    const start = rawFromMatch(match[1], match[2], match[3]);
    const end = rawFromMatch(match[4], match[5], match[6]);
    if (!start || !end || match.index == null) continue;
    pushRange(ranges, match.index, start, end, today, inclusive(match[0]));
  }

  const iso = new RegExp(
    String.raw`\b(\d{4})-(\d{2})-(\d{2})${RANGE_JOIN}(\d{4})-(\d{2})-(\d{2})\b`,
    "giu",
  );
  for (const match of flat.matchAll(iso)) {
    const start = rawFromMatch(match[3], match[2], match[1]);
    const end = rawFromMatch(match[6], match[5], match[4]);
    if (!start || !end || match.index == null) continue;
    pushRange(ranges, match.index, start, end, today, inclusive(match[0]));
  }

  const sharedMonth = new RegExp(
    String.raw`\b(\d{1,2})\.?\s+bis(?:\s+(?:zum|einschließlich|einschliesslich))?\s+(\d{1,2})\.(\d{1,2})\.(\d{2,4})\b`,
    "giu",
  );
  for (const match of flat.matchAll(sharedMonth)) {
    const end = rawFromMatch(match[2], match[3], match[4]);
    if (!end || match.index == null || !match[1]) continue;
    pushRange(
      ranges,
      match.index,
      { day: Number(match[1]), month: end.month, year: end.year },
      end,
      today,
      inclusive(match[0]),
    );
  }

  const compact = new RegExp(
    String.raw`\b(\d{1,2})\s*(?:[.\-–]\s*){1,3}(\d{1,2})\.(\d{1,2})\.(\d{2,4})\b`,
    "giu",
  );
  for (const match of flat.matchAll(compact)) {
    const end = rawFromMatch(match[2], match[3], match[4]);
    if (!end || match.index == null || !match[1]) continue;
    pushRange(
      ranges,
      match.index,
      { day: Number(match[1]), month: end.month, year: end.year },
      end,
      today,
      false,
    );
  }

  const monthWords = new RegExp(
    String.raw`\b(\d{1,2})\.?\s+(${MONTH_PATTERN})\b\.?(?:\s+(\d{4}))?\s+bis(?:\s+(?:zum|einschließlich|einschliesslich))?\s+(\d{1,2})\.?\s+(${MONTH_PATTERN})\b\.?(?:\s+(\d{4}))?`,
    "giu",
  );
  for (const match of flat.matchAll(monthWords)) {
    const startMonth = match[2] ? monthNumber(match[2]) : null;
    const endMonth = match[5] ? monthNumber(match[5]) : null;
    if (!startMonth || !endMonth || match.index == null || !match[1] || !match[4]) continue;
    pushRange(
      ranges,
      match.index,
      { day: Number(match[1]), month: startMonth, year: match[3] ? Number(match[3]) : null },
      { day: Number(match[4]), month: endMonth, year: match[6] ? Number(match[6]) : match[3] ? Number(match[3]) : null },
      today,
      inclusive(match[0]),
    );
  }

  const compactNamed = new RegExp(
    String.raw`\b(\d{1,2})\s*(?:[.\-–]\s*){1,3}(\d{1,2})\.?\s+(${MONTH_PATTERN})\b\.?(?:\s+(\d{4}))?`,
    "giu",
  );
  for (const match of flat.matchAll(compactNamed)) {
    const month = match[3] ? monthNumber(match[3]) : null;
    if (!month || match.index == null || !match[1] || !match[2]) continue;
    const year = match[4] ? Number(match[4]) : null;
    pushRange(
      ranges,
      match.index,
      { day: Number(match[1]), month, year },
      { day: Number(match[2]), month, year },
      today,
      false,
    );
  }

  const dayToNamedMonth = new RegExp(
    String.raw`\b(\d{1,2})\.?\s+bis(?:\s+(?:zum|einschließlich|einschliesslich))?\s+(\d{1,2})\.?\s+(${MONTH_PATTERN})\b\.?(?:\s+(\d{4}))?`,
    "giu",
  );
  for (const match of flat.matchAll(dayToNamedMonth)) {
    const month = match[3] ? monthNumber(match[3]) : null;
    if (!month || match.index == null || !match[1] || !match[2]) continue;
    const year = match[4] ? Number(match[4]) : null;
    pushRange(
      ranges,
      match.index,
      { day: Number(match[1]), month, year },
      { day: Number(match[2]), month, year },
      today,
      inclusive(match[0]),
    );
  }

  const nightWord = String.raw`übernachtungen|uebernachtungen|nächte|naechte|nacht`;
  const nights = new RegExp(
    String.raw`(?:(\d{1,2})\s*(?:${nightWord})\b.{0,40}?(\d{1,2})\.(\d{1,2})\.(?:(\d{2,4})\b)?|(\d{1,2})\.(\d{1,2})\.(?:(\d{2,4})\b)?.{0,40}?(\d{1,2})\s*(?:${nightWord})\b)`,
    "giu",
  );
  for (const match of flat.matchAll(nights)) {
    const nightsFirst = Boolean(match[1]);
    const count = Number(nightsFirst ? match[1] : match[8]);
    const raw = nightsFirst
      ? rawFromMatch(match[2], match[3], match[4])
      : rawFromMatch(match[5], match[6], match[7]);
    if (!raw || match.index == null || count < 1 || count > 60) continue;
    const arrival = resolveSingleDate(raw, today);
    if (!arrival) continue;
    ranges.push({
      index: match.index,
      arrival,
      departure: addDaysLocal(arrival, count),
      kind: "nights",
    });
  }

  const week = /\bkw\s*0?(\d{1,2})(?:\s*[/-]\s*(\d{4}))?\b/giu;
  for (const match of flat.matchAll(week)) {
    const weekNo = Number(match[1]);
    const year = match[2] ? Number(match[2]) : null;
    if (match.index == null || weekNo < 1 || weekNo > 53) continue;
    const resolved = isoWeekRange(weekNo, year, today);
    if (!resolved) continue;
    ranges.push({ index: match.index, ...resolved, kind: "week" });
  }

  ranges.sort((a, b) => a.index - b.index);
  const seen = new Set<string>();
  return ranges.filter((range) => {
    const key = `${range.arrival}|${range.departure}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function rawFromMatch(
  day: string | undefined,
  month: string | undefined,
  year: string | undefined,
): RawDate | null {
  if (!day || !month) return null;
  const parsedDay = Number(day);
  const parsedMonth = Number(month);
  if (parsedMonth < 1 || parsedMonth > 12 || parsedDay < 1 || parsedDay > 31) return null;
  return {
    day: parsedDay,
    month: parsedMonth,
    year: year ? Number(year) : null,
  };
}

function firstRawDate(fragment: string): RawDate | null {
  const numeric = fragment.match(/\b(\d{1,2})\.(\d{1,2})\.(?:(\d{2,4})\b)?/);
  const fromNumeric = rawFromMatch(numeric?.[1], numeric?.[2], numeric?.[3]);
  if (fromNumeric) return fromNumeric;
  const iso = fragment.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso?.[1] && iso[2] && iso[3]) return rawFromMatch(iso[3], iso[2], iso[1]);
  const named = fragment.match(
    new RegExp(String.raw`\b(\d{1,2})\.?\s+(${MONTH_PATTERN})\b\.?(?:\s+(\d{4}))?`, "iu"),
  );
  const month = named?.[2] ? monthNumber(named[2]) : null;
  if (!named?.[1] || !month) return null;
  return { day: Number(named[1]), month, year: named[3] ? Number(named[3]) : null };
}

function isoWeekMonday(year: number, week: number): string | null {
  if (week < 1 || week > 53) return null;
  const jan4 = new Date(year, 0, 4);
  const weekday = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - weekday + 1 + (week - 1) * 7);
  const iso = isoFromParts(monday.getDate(), monday.getMonth() + 1, monday.getFullYear());
  if (!iso) return null;
  const check = new Date(`${iso}T12:00:00`);
  const thursday = new Date(check);
  thursday.setDate(check.getDate() + 3);
  if (thursday.getFullYear() !== year && week === 53) return null;
  return iso;
}

function isoWeekRange(
  week: number,
  year: number | null,
  today: string,
): { arrival: string; departure: string } | null {
  const thisYear = Number(today.slice(0, 4));
  const years = year != null ? [year] : [thisYear, thisYear + 1];
  for (const candidate of years) {
    const arrival = isoWeekMonday(candidate, week);
    if (!arrival || arrival < today) continue;
    return { arrival, departure: addDaysLocal(arrival, 7) };
  }
  return null;
}

function flattenMail(text: string): string {
  return text.replace(/[–—]/g, "-").replace(/\s+/g, " ").trim();
}

export function parseStayDates(text: string, today = todayLocal()): { arrival: string; departure: string } | null {
  const flat = flattenMail(text);
  const ranges = collectStayRanges(flat, today);
  const labeled = ranges.find((range) => {
    const window = flat.slice(range.index, range.index + 12).toLocaleLowerCase("de");
    return window.startsWith("anreise") || window.startsWith("ankunft") || window.startsWith("check");
  });
  const explicit = ranges.find((range) => range.kind === "range");
  const picked = labeled ?? explicit ?? ranges[0];
  if (!picked) return null;
  return { arrival: picked.arrival, departure: picked.departure };
}

export function looksLikeErasureRequest(subject: string, body: string): boolean {
  const hay = `${subject}\n${body}`.toLocaleLowerCase("de");
  return /lösch|loesch|datenschutz|dsgvo|gdpr|art\.?\s*1[67]|recht auf vergessen|personenbezogen/.test(hay);
}

function parseCountToken(token: string | undefined): number | null {
  if (!token) return null;
  const normalized = token.trim().toLocaleLowerCase("de");
  if (/^\d+$/.test(normalized)) {
    const value = Number(normalized);
    return value >= 0 && value <= 20 ? value : null;
  }
  return WORD_NUMBERS[normalized] ?? null;
}

function parseParty(flat: string): { adults: number | null; children: number | null; dogs: number | null } {
  const adultsMatch = flat.match(
    new RegExp(String.raw`\b(${COUNT_WORD})\s*(?:erwachsene[rn]?|erw\.?)(?!\p{L})`, "iu"),
  );
  const childrenMatch = flat.match(
    new RegExp(String.raw`\b(${COUNT_WORD})\s*(?:kinder|kindern|kind)(?!\p{L})`, "iu"),
  );
  const dogsMatch = flat.match(
    new RegExp(String.raw`\b(${COUNT_WORD})\s*hund(?:e|en)?(?!\p{L})`, "iu"),
  );
  const personsMatch = flat.match(
    new RegExp(
      String.raw`\b(?:wir\s+sind|sind\s+wir|insgesamt)\s+(${COUNT_WORD})\s*(?:personen|erwachsene|leute|gäste|gaeste)\b`,
      "iu",
    ),
  );
  const groupMatch = flat.match(/\bzu\s+(zweit|dritt|viert|fünft|fuenft|sechst|siebt|acht)\b/iu);
  const groupSize: Record<string, number> = {
    zweit: 2,
    dritt: 3,
    viert: 4,
    fünft: 5,
    fuenft: 5,
    sechst: 6,
    siebt: 7,
    acht: 8,
  };

  let adults = parseCountToken(adultsMatch?.[1]);
  if (adults === 0) adults = null;
  let children = /keine[n]?\s+kinder|ohne\s+kinder/iu.test(flat) ? 0 : parseCountToken(childrenMatch?.[1]);
  let dogs = /ohne\s+hund|kein(?:en|e)?\s+hund/iu.test(flat) ? 0 : parseCountToken(dogsMatch?.[1]);
  if (dogs == null && /\bmit\s+(?:einem\s+|unserem\s+)?hund\b/iu.test(flat)) dogs = 1;

  if (adults == null && /allein(?:e)?\b/iu.test(flat)) adults = 1;
  if (adults == null && groupMatch?.[1]) {
    adults = groupSize[groupMatch[1].toLocaleLowerCase("de")] ?? null;
  }
  const persons = parseCountToken(personsMatch?.[1]);
  if (persons != null && persons >= 1) {
    if (adults == null && children == null) adults = persons;
    else if (adults != null && children == null && persons > adults) children = persons - adults;
  }
  return { adults, children, dogs };
}

function parseArrivalType(flat: string): string {
  const hay = flat.toLocaleLowerCase("de");
  if (/wohnmobil|motorhome|\bwomo\b|\bcamper\b/.test(hay)) return "motorhome";
  if (/wohnwagen|\bcaravan\b|\bwowa\b/.test(hay)) return "caravan";
  if (/zelt/.test(hay) && /fahrrad|radfahrer|mit dem rad/.test(hay)) return "tent_bike";
  if (/zelt/.test(hay) && /\bauto\b|\bpkw\b/.test(hay)) return "tent_car";
  if (/\bzelt(?:e)?\b/.test(hay)) return "tent";
  if (/zugfahrzeug/.test(hay)) return "tow_vehicle";
  return "";
}

function parseElectricity(flat: string): boolean | null {
  if (/ohne\s+strom|kein(?:en)?\s+strom/iu.test(flat)) return false;
  if (/mit\s+strom|stromanschluss|\belektro\b/iu.test(flat)) return true;
  return null;
}

function partySummary(adults: number | null, children: number | null, dogs: number | null): string {
  const parts: string[] = [];
  if (adults != null) parts.push(`${adults} ${adults === 1 ? "Erwachsener" : "Erwachsene"}`);
  if (children != null) parts.push(`${children} ${children === 1 ? "Kind" : "Kinder"}`);
  if (dogs != null) {
    parts.push(dogs === 0 ? "ohne Hund" : `${dogs} ${dogs === 1 ? "Hund" : "Hunde"}`);
  }
  return parts.join(", ");
}

export function analyzeInquiryMail(input: {
  subject: string;
  body: string;
  categories: PitchCategory[];
  today?: string;
}): MailInquiryAnalysis {
  const today = input.today ?? todayLocal();
  const erasureRequest = looksLikeErasureRequest(input.subject, "");
  const flat = flattenMail(`${input.subject}\n${input.body}`);
  const dates = erasureRequest ? null : parseStayDates(flat, today);
  const party = erasureRequest
    ? { adults: null, children: null, dogs: null }
    : parseParty(flat);
  const arrivalType = erasureRequest ? "" : parseArrivalType(flat);
  const categoryId = erasureRequest ? "" : matchPitchCategory(flat, input.categories);
  const categoryName =
    input.categories.find((category) => category.id === categoryId)?.name ?? "";
  const wantsElectricity = erasureRequest ? null : parseElectricity(flat);
  const summaryParts = [
    dates ? `${formatIsoDateShort(dates.arrival)} – ${formatIsoDateShort(dates.departure)}` : "",
    partySummary(party.adults, party.children, party.dogs),
    arrivalType ? ARRIVAL_TYPE_LABELS[arrivalType] ?? arrivalType : "",
    categoryName,
    wantsElectricity == null ? "" : wantsElectricity ? "mit Strom" : "ohne Strom",
  ].filter(Boolean);

  return {
    erasureRequest,
    arrivalDate: dates?.arrival ?? "",
    departureDate: dates?.departure ?? "",
    adults: party.adults,
    children: party.children,
    dogs: party.dogs,
    arrivalType,
    categoryId,
    categoryName,
    wantsElectricity,
    summary: summaryParts.join(" · "),
  };
}

function parseClosingName(body: string): { firstName: string; lastName: string } | null {
  const match = body.match(
    /(?:mit freundlichen grüßen|mit freundlichen gruessen|freundliche grüße|freundliche gruesse|viele grüße|viele gruesse|liebe grüße|herzliche grüße|mfg)\s*[,!]?\s*[\r\n]+\s*([A-ZÄÖÜ][\p{L}'’\-]+)\s+([A-ZÄÖÜ][\p{L}'’\-]+)/iu,
  );
  const firstName = match?.[1]?.trim();
  const lastName = match?.[2]?.trim();
  if (!firstName || !lastName) return null;
  if (firstName.length < 2 || lastName.length < 2) return null;
  return { firstName, lastName };
}

export function matchPitchCategory(text: string, categories: PitchCategory[]): string {
  const haystack = text.toLocaleLowerCase("de");
  const hits = categories
    .filter((category) => category.is_active !== false)
    .map((category) => {
      const names = [category.name, category.name_en ?? ""].map((name) => name.trim()).filter((name) => name.length >= 4);
      const matched = names.find((name) => haystack.includes(name.toLocaleLowerCase("de")));
      return matched ? { category, length: matched.length } : null;
    })
    .filter((hit): hit is { category: PitchCategory; length: number } => hit != null)
    .sort((a, b) => b.length - a.length);
  return hits[0]?.category.id ?? "";
}

function guestFieldValue(guest: Guest, key: CompanionFieldKey): string {
  if (key === "license_plate" || key === "age") return "";
  if (key === "phone") return (guest.phone || guest.phone_mobile || "").trim();
  const raw = guest[key as keyof Guest];
  if (raw == null) return "";
  const text = String(raw).trim();
  return text === "–" ? "" : text;
}

export function guestDataGaps(
  guest: Guest,
  fields: CompanionFieldsConfig,
): { missingLabels: string[]; presentLabels: string[] } {
  const missingLabels: string[] = [];
  const presentLabels: string[] = [];
  for (const row of COMPANION_FIELD_ROWS) {
    if (fields[row.key] !== "required") continue;
    if (row.key === "license_plate" || row.key === "age") continue;
    const value = guestFieldValue(guest, row.key);
    if (value) presentLabels.push(row.label);
    else missingLabels.push(row.label);
  }
  return { missingLabels, presentLabels };
}

export function depositHint(deposit: DepositSettings, onlinePayment: boolean): string {
  const mode =
    deposit.mode === "fixed"
      ? `Pauschale ${deposit.fixed_amount ?? "0"} €`
      : deposit.mode === "first_night"
        ? "erste Nacht"
        : `${deposit.percent} %`;
  const payment = onlinePayment
    ? "Der Gast kann die Anzahlung im Link direkt online leisten."
    : "Online-Zahlung ist nicht eingerichtet. Der Gast sieht den Anzahlungsbetrag und bestätigt die Buchung.";
  return `Anzahlung: ${mode}. ${payment}`;
}

export function primaryGuestFieldsFromSettings(
  raw: Partial<CompanionFieldsConfig> | undefined,
): CompanionFieldsConfig {
  return normalizeGuestAddressFieldModes({
    ...DEFAULT_PRIMARY_GUEST_FIELDS,
    ...raw,
  });
}

export function buildMailReplySeed(input: {
  email: string;
  subject: string;
  body: string;
  guest: Guest | null;
  categories: PitchCategory[];
  fields: CompanionFieldsConfig;
  deposit: DepositSettings;
  onlinePayment: boolean;
}): MailReplySeed {
  const analysis = analyzeInquiryMail({
    subject: input.subject,
    body: input.body,
    categories: input.categories,
  });
  const signed = input.guest ? null : parseClosingName(input.body);
  const gaps = input.guest
    ? guestDataGaps(input.guest, input.fields)
    : { missingLabels: [], presentLabels: [] };
  const excerpt = input.body.replace(/\s+/g, " ").trim().slice(0, 240);
  const notes = (excerpt ? `E-Mail „${input.subject}“: ${excerpt}` : `E-Mail „${input.subject}“`).slice(0, 1800);
  const guestFirst = input.guest?.first_name && input.guest.first_name !== "–" ? input.guest.first_name : "";
  const guestLast = input.guest?.last_name && input.guest.last_name !== "–" ? input.guest.last_name : "";
  return {
    email: input.email,
    firstName: guestFirst || signed?.firstName || "",
    lastName: guestLast || signed?.lastName || "",
    arrivalDate: analysis.arrivalDate,
    departureDate: analysis.departureDate,
    categoryId: analysis.categoryId,
    arrivalType: analysis.arrivalType,
    adults: analysis.adults,
    children: analysis.children,
    dogs: analysis.dogs,
    wantsElectricity: analysis.wantsElectricity,
    guestId: input.guest?.id ?? "",
    notes,
    placeBanned: Boolean(input.guest?.is_place_banned),
    missingLabels: gaps.missingLabels,
    presentLabels: gaps.presentLabels,
    depositHint: depositHint(input.deposit, input.onlinePayment),
  };
}
